import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Line } from "@react-three/drei";
import * as THREE from "three";import { Bot, GitBranch, Users, MessageSquare, TrendingUp } from "lucide-react";

const ROTATING_WORDS = [
    "AI Agents",
    "CRM",
    "Order Confirmation",
    "AI Image Detection",
    "Automated Workflows",
    "API Connectivity",
];

const ORBIT_ICONS = [
    { src: "/wa.jpg", label: "WhatsApp" },
    { src: "/instagram.jpg", label: "Instagram" },
    { src: "/shopify.jpg", label: "Shopify" },
    { src: "/facebook.jpg", label: "Facebook" },
    { src: "/postex.webp", label: "PostEx" },
];

const FEATURE_ICONS = [
    { Icon: Bot, label: "AI Bot" },
    { Icon: GitBranch, label: "Workflows" },
    { Icon: Users, label: "CRM" },
    { Icon: MessageSquare, label: "Live Chat" },
    { Icon: TrendingUp, label: "Analytics" },
];

function arcPosition(index, total, startDeg, endDeg, radius = 42) {
    const t = total === 1 ? 0.5 : index / (total - 1);
    const deg = startDeg + (endDeg - startDeg) * t;
    const angle = (deg * Math.PI) / 180;
    const left = 50 + radius * Math.cos(angle);
    const top = 50 + radius * Math.sin(angle);
    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: "translate(-50%, -50%)",
    };
}

function RotatingWord() {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setIndex((i) => (i + 1) % ROTATING_WORDS.length);
        }, 1800);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="relative h-5 sm:h-6 overflow-hidden">
            <div
                key={index}
                className="absolute inset-0 flex items-center justify-center gap-1.5 text-[9px] sm:text-sm font-medium text-white/90 tracking-tight sm:tracking-wide whitespace-nowrap animate-word-rotate"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}
            >
                <span
                    className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"
                    style={{ boxShadow: "0 0 8px 2px rgba(96,165,250,0.9), 0 0 16px 4px rgba(59,130,246,0.5)" }}
                />
                {ROTATING_WORDS[index]}
            </div>
        </div>
    );
}

function OrbitIcons() {
    return (
        <>
            {ORBIT_ICONS.map((item, i) => {
                const style = arcPosition(i, ORBIT_ICONS.length, -160, -20);
                return (
                    <div
                        key={item.label}
                        className="absolute z-20 pointer-events-none flex flex-col items-center gap-0.5"
                        style={style}
                    >
                        <div
                            className="bg-white/95 backdrop-blur-sm rounded-full shadow-xl shadow-blue-500/20 border border-white/60 flex items-center justify-center overflow-hidden
                                       w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12
                                       animate-icon-float"
                        >
                            <img
                                src={item.src}
                                alt={item.label}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <span className="text-[6px] sm:text-[8px] md:text-[9px] font-bold text-blue-700 uppercase tracking-tight sm:tracking-wider whitespace-nowrap">
                            {item.label}
                        </span>
                    </div>
                );
            })}
        </>
    );
}

function FeatureOrbitIcons() {
    return (
        <>
            {FEATURE_ICONS.map((item, i) => {
                const style = arcPosition(i, FEATURE_ICONS.length, 160, 20);
                return (
                    <div
                        key={item.label}
                        className="absolute z-20 pointer-events-none flex flex-col items-center gap-0.5"
                        style={style}
                    >
                        <div
                            className="bg-white/95 backdrop-blur-sm rounded-full shadow-xl shadow-blue-500/20 border border-white/60 flex items-center justify-center
                                       w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12
                                       animate-icon-float"
                        >
                            <item.Icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </div>
                        <span className="text-[6px] sm:text-[8px] md:text-[9px] font-bold text-blue-700 uppercase tracking-tight sm:tracking-wider whitespace-nowrap">
                            {item.label}
                        </span>
                    </div>
                );
            })}
        </>
    );
}

function CenterLabel() {
    return (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none px-4">
            <img
                src="/new_logo.png"
                alt="Vetobase AI"
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)]"
            />
            <div
                className="mt-1 sm:mt-1.5 text-white font-black tracking-tight text-base sm:text-lg md:text-xl lg:text-2xl leading-none"
                style={{
                    textShadow:
                        "0 2px 6px rgba(0,0,0,0.55), 0 0 18px rgba(10,20,80,0.7)",
                }}
            >
                Vetobase AI
            </div>

            <div className="mt-1 sm:mt-1.5 w-44 sm:w-48 md:w-56">
                <RotatingWord />
            </div>
        </div>
    );
}

const SPHERE_R = 1;
const BEAM_SEG = 24;
const RIPPLE_SEG = 80;
const MAX_CLICK_RIPPLES = 6;
const CLICK_DURATION = 1.05;

function makeSlots(n) {
    return Array.from({ length: n }, () => ({
        active: false,
        t0: -1,
        impact: new THREE.Vector3(),
        u: new THREE.Vector3(),
        v: new THREE.Vector3(),
    }));
}

function bz(t, p0, p1, p2, out) {
    const u = 1 - t;
    const uu = u * u;
    const tt = t * t;
    const ut2 = 2 * u * t;
    out.x = uu * p0.x + ut2 * p1.x + tt * p2.x;
    out.y = uu * p0.y + ut2 * p1.y + tt * p2.y;
    out.z = uu * p0.z + ut2 * p1.z + tt * p2.z;
}

function tangentBasis(n, outU, outV) {
    const tmp =
        Math.abs(n.x) > 0.95
            ? new THREE.Vector3(0, 1, 0)
            : new THREE.Vector3(1, 0, 0);
    outU.crossVectors(tmp, n).normalize();
    outV.crossVectors(n, outU).normalize();
}

function writeRipple(arr, impact, u, v, theta) {
    const cosT = Math.cos(theta);
    const sinT = Math.sin(theta);
    const out = 1.012;
    for (let i = 0; i <= RIPPLE_SEG; i++) {
        const phi = (i / RIPPLE_SEG) * Math.PI * 2;
        const cp = Math.cos(phi);
        const sp = Math.sin(phi);
        const tx = u.x * cp + v.x * sp;
        const ty = u.y * cp + v.y * sp;
        const tz = u.z * cp + v.z * sp;
        arr[i * 3] = (impact.x * cosT + tx * sinT) * out;
        arr[i * 3 + 1] = (impact.y * cosT + ty * sinT) * out;
        arr[i * 3 + 2] = (impact.z * cosT + tz * sinT) * out;
    }
}

function Orb({ onImpact }) {
    const mesh = useRef(null);

    const setCursor = (v) => {
        if (typeof document !== "undefined") document.body.style.cursor = v;
    };

    return (
        <mesh
            ref={mesh}
            onPointerOver={(e) => {
                e.stopPropagation();
                setCursor("pointer");
            }}
            onPointerOut={(e) => {
                e.stopPropagation();
                setCursor("default");
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!mesh.current) return;
                const local = mesh.current.worldToLocal(e.point.clone());
                const impact = local.normalize().multiplyScalar(SPHERE_R);
                onImpact(impact);
            }}
        >
            <sphereGeometry args={[SPHERE_R, 256, 256]} />
            <meshPhysicalMaterial
                color="#2D5BFF"
                roughness={0.42}
                metalness={0.05}
                clearcoat={0.35}
                clearcoatRoughness={0.55}
                sheen={0.4}
                sheenColor="#7FA0FF"
                sheenRoughness={0.6}
                emissive="#1B3FCC"
                emissiveIntensity={0.08}
            />
        </mesh>
    );
}

function FallingBeam({ seed, speed }) {
    const beamCoreRef = useRef(null);
    const beamGlowRef = useRef(null);
    const rippleCoreRef = useRef(null);
    const rippleGlowRef = useRef(null);

    const beamPos = useMemo(() => new Float32Array(BEAM_SEG * 3), []);
    const ripplePos = useMemo(
        () => new Float32Array((RIPPLE_SEG + 1) * 3),
        [],
    );
    const tmpRef = useRef(new THREE.Vector3());

    const stateRef = useRef({
        impact: new THREE.Vector3(),
        start: new THREE.Vector3(),
        control: new THREE.Vector3(),
        u: new THREE.Vector3(),
        v: new THREE.Vector3(),
        initialized: false,
    });

    const respawn = () => {
        const s = stateRef.current;
        s.impact
            .set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
            .normalize()
            .multiplyScalar(SPHERE_R);

        const n = s.impact.clone().normalize();
        const r = new THREE.Vector3(
            Math.random() - 0.5,
            Math.random() - 0.5,
            Math.random() - 0.5,
        ).normalize();
        if (r.dot(n) < 0.4) r.copy(n).addScaledVector(r, 0.6).normalize();
        s.start.copy(s.impact).addScaledVector(r, 1.7 + Math.random() * 0.8);

        const dir = s.impact.clone().sub(s.start);
        const dirLen = dir.length();
        const dirN = dir.clone().normalize();
        const rand = new THREE.Vector3(
            Math.random() - 0.5,
            Math.random() - 0.5,
            Math.random() - 0.5,
        ).normalize();
        const perp = dirN.clone().cross(rand).normalize();
        const curvature = dirLen * (0.3 + Math.random() * 0.35);
        s.control
            .copy(s.start)
            .lerp(s.impact, 0.5)
            .addScaledVector(perp, curvature);

        tangentBasis(n, s.u, s.v);
        s.initialized = true;
    };

    useMemo(() => {
        respawn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useFrame((rs) => {
        const t = rs.clock.getElapsedTime();
        const phase = (t * speed + seed) % 1;
        const s = stateRef.current;
        if (!s.initialized) return;

        const beamEnd = 0.55;
        const rippleStart = 0.55;
        const rippleEnd = 0.98;

        if (phase < 0.015) respawn();

        if (beamCoreRef.current && beamGlowRef.current) {
            const coreMat = beamCoreRef.current.material;
            const glowMat = beamGlowRef.current.material;
            if (phase < beamEnd) {
                const bp = phase / beamEnd;
                const headT = bp;
                const tailT = Math.max(0, bp - 0.28);
                const tmp = tmpRef.current;
                for (let i = 0; i < BEAM_SEG; i++) {
                    const tt = tailT + (headT - tailT) * (i / (BEAM_SEG - 1));
                    bz(tt, s.start, s.control, s.impact, tmp);
                    beamPos[i * 3] = tmp.x;
                    beamPos[i * 3 + 1] = tmp.y;
                    beamPos[i * 3 + 2] = tmp.z;
                }
                beamCoreRef.current.geometry?.setPositions?.(beamPos);
                beamGlowRef.current.geometry?.setPositions?.(beamPos);
                const fade = bp < 0.06 ? bp / 0.06 : 1;
                coreMat.opacity = fade;
                glowMat.opacity = fade * 0.85;
            } else {
                coreMat.opacity = 0;
                glowMat.opacity = 0;
            }
        }

        if (rippleCoreRef.current && rippleGlowRef.current) {
            const coreMat = rippleCoreRef.current.material;
            const glowMat = rippleGlowRef.current.material;
            if (phase >= rippleStart && phase < rippleEnd) {
                const rp = (phase - rippleStart) / (rippleEnd - rippleStart);
                writeRipple(ripplePos, s.impact, s.u, s.v, rp * 0.95);
                rippleCoreRef.current.geometry?.setPositions?.(ripplePos);
                rippleGlowRef.current.geometry?.setPositions?.(ripplePos);
                const flash =
                    rp < 0.06 ? rp / 0.06 : Math.max(0, 1 - (rp - 0.06) / 0.94);
                coreMat.opacity = flash;
                glowMat.opacity = flash * 0.9;
            } else {
                coreMat.opacity = 0;
                glowMat.opacity = 0;
            }
        }
    });

    const beamInit = useMemo(
        () =>
            Array.from(
                { length: BEAM_SEG },
                (_, i) => new THREE.Vector3(i * 0.001, 0, 0),
            ),
        [],
    );
    const rippleInit = useMemo(
        () =>
            Array.from({ length: RIPPLE_SEG + 1 }, (_, i) => {
                const a = (i / RIPPLE_SEG) * Math.PI * 2;
                return new THREE.Vector3(Math.cos(a) * 0.01, Math.sin(a) * 0.01, 0);
            }),
        [],
    );

    return (
        <group>
            <Line
                ref={beamGlowRef}
                points={beamInit}
                color="#5B7FFF"
                lineWidth={7}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
            <Line
                ref={beamCoreRef}
                points={beamInit}
                color="#E6EEFF"
                lineWidth={2.2}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
            <Line
                ref={rippleGlowRef}
                points={rippleInit}
                color="#5B7FFF"
                lineWidth={6}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
            <Line
                ref={rippleCoreRef}
                points={rippleInit}
                color="#E6EEFF"
                lineWidth={1.8}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
        </group>
    );
}

function FallingBeams({ count = 5 }) {
    const beams = useMemo(
        () =>
            Array.from({ length: count }, (_, i) => ({
                seed: Math.random() * 100 + i,
                speed: 0.3 + Math.random() * 0.35,
            })),
        [count],
    );
    return (
        <>
            {beams.map((b, i) => (
                <FallingBeam key={i} seed={b.seed} speed={b.speed} />
            ))}
        </>
    );
}

function ClickRipple({ slot }) {
    const coreRef = useRef(null);
    const glowRef = useRef(null);
    const ripplePos = useMemo(
        () => new Float32Array((RIPPLE_SEG + 1) * 3),
        [],
    );

    useFrame((rs) => {
        const coreMat = coreRef.current?.material;
        const glowMat = glowRef.current?.material;
        if (!coreMat || !glowMat) return;

        if (!slot.active) {
            coreMat.opacity = 0;
            glowMat.opacity = 0;
            return;
        }

        const t = rs.clock.getElapsedTime();
        if (slot.t0 < 0) slot.t0 = t;
        const elapsed = t - slot.t0;
        const rp = elapsed / CLICK_DURATION;

        if (rp >= 1) {
            slot.active = false;
            slot.t0 = -1;
            coreMat.opacity = 0;
            glowMat.opacity = 0;
            return;
        }

        writeRipple(ripplePos, slot.impact, slot.u, slot.v, rp * 1.15);
        coreRef.current.geometry?.setPositions?.(ripplePos);
        glowRef.current.geometry?.setPositions?.(ripplePos);

        const flash =
            rp < 0.05 ? rp / 0.05 : Math.max(0, 1 - (rp - 0.05) / 0.95);
        coreMat.opacity = flash;
        glowMat.opacity = flash;
    });

    const init = useMemo(
        () =>
            Array.from({ length: RIPPLE_SEG + 1 }, (_, i) => {
                const a = (i / RIPPLE_SEG) * Math.PI * 2;
                return new THREE.Vector3(Math.cos(a) * 0.01, Math.sin(a) * 0.01, 0);
            }),
        [],
    );

    return (
        <>
            <Line
                ref={glowRef}
                points={init}
                color="#7FA0FF"
                lineWidth={8}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
            <Line
                ref={coreRef}
                points={init}
                color="#FFFFFF"
                lineWidth={2.2}
                transparent
                opacity={0}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
            />
        </>
    );
}

function ClickRipples({ slotsRef }) {
    return (
        <>
            {slotsRef.current.map((slot, i) => (
                <ClickRipple key={i} slot={slot} />
            ))}
        </>
    );
}

export function Sphere() {
    const clickSlotsRef = useRef(makeSlots(MAX_CLICK_RIPPLES));

    const triggerImpact = (impact) => {
        const slot = clickSlotsRef.current.find((s) => !s.active);
        if (!slot) return;
        slot.active = true;
        slot.t0 = -1;
        slot.impact.copy(impact);
        const n = impact.clone().normalize();
        tangentBasis(n, slot.u, slot.v);
    };

    return (
        <div className="relative h-full w-full">
            <div className="absolute inset-0 sphere-halo-deep blur-3xl" />
            <div className="absolute inset-0 sphere-halo blur-3xl" />
            <div className="absolute inset-0 sphere-halo blur-2xl opacity-70" />

            <Canvas
                camera={{ position: [0, 0, 4.2], fov: 36 }}
                dpr={[1, 2]}
                gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
                className="!h-full !w-full"
            >
                <ambientLight intensity={0.55} />
                <directionalLight
                    position={[-3, 4, 4]}
                    intensity={2.6}
                    color="#FFFFFF"
                />
                <directionalLight
                    position={[2, -2, -1]}
                    intensity={0.5}
                    color="#7FA0FF"
                />
                <pointLight position={[0, 0, 3]} intensity={0.3} color="#FFFFFF" />

                <Suspense fallback={null}>
                    <Float speed={1} rotationIntensity={0.1} floatIntensity={0.35}>
                        <group scale={0.85}>
                            <Orb onImpact={triggerImpact} />
                            <FallingBeams count={5} />
                            <ClickRipples slotsRef={clickSlotsRef} />
                        </group>
                    </Float>
                </Suspense>
            </Canvas>

            <CenterLabel />
            <OrbitIcons />
            <FeatureOrbitIcons />
        </div>
    );
}

export default Sphere;
